import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const MAX_RECENT_POSTS = 8

const normalizePostKeyPart = (value: unknown) => String(value ?? '').trim().toLowerCase()

const getPostServiceInstanceKey = (post: Post | null | undefined) => normalizePostKeyPart(
  post?.service_instance ?? post?.service_instance_id,
)

const isOrganizationScopePost = (post: Post | null | undefined) => {
  const variantUniqueId = normalizePostKeyPart(post?.service_variant_unique_id)
  const variantLabel = normalizePostKeyPart(post?.service_variant_name ?? post?.service_variant_type)
  return ['100', '102', 'org', 'organization', 'root-org', 'root_org', 'tenant'].includes(variantUniqueId)
    || ['org', 'organization', 'root-org', 'root_org', 'tenant'].includes(variantLabel)
}

const getBranchServiceInstanceKeys = (branch: Branch | null | undefined) => [
  branch?.instanceId,
  branch?.serviceId,
  branch?.code,
  branch?.id,
].map(normalizePostKeyPart).filter(Boolean)

const findBranchForPost = (branches: Branch[], post: Post | null | undefined) => {
  const postServiceInstanceKey = getPostServiceInstanceKey(post)
  if (!postServiceInstanceKey) {
    return null
  }

  return branches.find((branch) => getBranchServiceInstanceKeys(branch).includes(postServiceInstanceKey)) ?? null
}

const isBranchAdminPost = (post: Post | null | undefined) => {
  const uniqueId = normalizePostKeyPart(post?.roles_data?.unique_id)
  const title = normalizePostKeyPart(post?.role_name ?? post?.roles_data?.roles_name)
  return uniqueId.startsWith('bradm') || title === 'branch admin'
}

const replacementForHiddenBranchAdmin = (posts: Post[]) => (
  posts.find((post) => normalizePostKeyPart(post.roles_data?.unique_id) === 'socadm2900')
  ?? posts.find((post) => normalizePostKeyPart(post.role_name ?? post.roles_data?.roles_name) === 'admin')
  ?? posts[0]
  ?? null
)

const getPostRoleIdentityKey = (post: Post | null | undefined) => {
  const stableRoleId = normalizePostKeyPart(post?.role_id ?? post?.roles_data?.unique_id)
  if (stableRoleId) {
    return stableRoleId
  }

  return [
    normalizePostKeyPart(post?.role_name ?? post?.roles_data?.roles_name),
    normalizePostKeyPart(post?.service_variant_unique_id ?? post?.service_variant_name ?? post?.service_variant_type),
    normalizePostKeyPart(post?.org_name),
    normalizePostKeyPart(post?.service_instance_name),
  ].join('|')
}

const isUsablePost = (post: Post | null | undefined): post is Post => {
  if (!post || typeof post !== 'object') {
    return false
  }

  const postId = normalizePostKeyPart(post.id)
  const roleLabel = normalizePostKeyPart(post.role_name ?? post.roles_data?.roles_name)

  return Boolean(postId && roleLabel)
}

const sanitizePosts = (posts: Post[] | null | undefined): Post[] => {
  if (!Array.isArray(posts)) {
    return []
  }

  return posts.filter((post): post is Post => isUsablePost(post))
}

export const getBookmarkedPostKey = (post: Post | null | undefined) => {
  if (!post) {
    return ''
  }

  return [
    getPostServiceInstanceKey(post),
    getPostRoleIdentityKey(post),
  ].join('::')
}

const isSamePost = (left: Post | null | undefined, right: Post | null | undefined) => {
  if (!left || !right) {
    return false
  }

  if (left._id != null && right._id != null && String(left._id) === String(right._id)) {
    return true
  }

  const leftServiceInstance = getPostServiceInstanceKey(left)
  const rightServiceInstance = getPostServiceInstanceKey(right)

  if (String(left.id) === String(right.id) && (!leftServiceInstance || !rightServiceInstance || leftServiceInstance === rightServiceInstance)) {
    return true
  }

  if (!leftServiceInstance || leftServiceInstance !== rightServiceInstance) {
    return false
  }

  const leftRoleIdentity = getPostRoleIdentityKey(left)
  const rightRoleIdentity = getPostRoleIdentityKey(right)
  return Boolean(leftRoleIdentity) && leftRoleIdentity === rightRoleIdentity
}

export interface User {
  id: number | string
  email: string
  phone_number?: string
  phone?: string
  first_name: string
  last_name: string
  username?: string
  avatar?: string
  avatar_url?: string
  full_name?: string
  role?: string
  date_joined?: string
  last_login?: string
  status?: string | number | null
  registration_status?: string | number | null
}

export interface Branch {
  id: number | string
  name: string
  serviceId?: string
  branchId?: string | number
  tenantId?: string | number
  primaryNode?: number | string
  code?: string
  instanceId?: string
  parentInstanceId?: string
  identifier?: string
  address?: string
  nodeName?: string
  stateName?: string
  bannerUrl?: string
  logoUrl?: string
  countryId?: string | number
  countryName?: string
  countryCode?: string
  timeZoneId?: string | number
  timeZone?: string
  currencySymbol?: string
  currencyCode?: string
  createdOn?: string
  createdByUserId?: number | string
  createdByName?: string
  status?: string | number
  serviceVariant?: string | number
  serviceVariantName?: string
  branchOwnership?: string | number
  branchOwnershipLabel?: string
  isVendorBranch?: boolean
  isActive?: boolean
  canDelete?: boolean
  usageCount?: number
  linkedJobCount?: number
  linkedLocationCount?: number
  linkedWorkerCount?: number
  linkedRoleAssignmentCount?: number
  linkedUserCount?: number
  adminUserId?: number | string
  adminName?: string
  adminEmail?: string
  adminPhone?: string
  supportUserId?: number | string
  supportName?: string
  supportEmail?: string
  supportPhone?: string
  levelVariantName?: string | null
  levelVariantTierCode?: string | null
}

export interface Post {
  id: number | string
  role_id?: number | string
  role_name: string
  _id?: number | string
  node_id?: number | string
  service_instance?: string | number | null
  service_instance_id?: string | number | null
  org_name?: string | null
  service_variant_name?: string | null
  service_variant_type?: string | null
  service_variant_service?: string | null
  service_variant_unique_id?: string | number | null
  level_variant_unique_id?: string | null
  /** When posts API includes org catalog fields (optional). */
  level_variant_name?: string | null
  level_variant_vertical_system_code?: string | null
  service_instance_name?: string | null
  service_instance_status?: string | number | null
  org_status?: string | number | null
  org_logo?: string | null
  service_instance_logo?: string | null
  service_instance_banner?: string | null
  roles_data?: {
    id?: number | string | null
    roles_name?: string | null
    unique_id?: string | null
    role_level?: number | null
    role_level_code?: string | null
    role_level_label?: string | null
    level_variant_unique_id?: string | null
    level_variant_name?: string | null
    tier_code?: string | null
    level_variant_vertical_system_code?: string | null
    permissions?: Record<string, unknown> | null
  } | null
  capabilities?: {
    can_view_business_dashboard?: boolean
    can_view_organization_tree?: boolean
    can_access_organization_workspace?: boolean
    can_access_organization_verticals?: boolean
    can_view_branch_directory?: boolean
    can_access_branch_management?: boolean
    can_view_tenant_directory?: boolean
    can_view_vendor_directory?: boolean
    can_view_user_directory?: boolean
    can_access_users?: boolean
    can_view_retail_customer_directory?: boolean
    can_view_business_customer_directory?: boolean
    can_view_client_directory?: boolean
    can_view_supplier_directory?: boolean
    can_view_customer_directory?: boolean
    can_view_role_directory?: boolean
    can_list_role_assignments?: boolean
    can_manage_roles?: boolean
    can_access_legacy_node_pages?: boolean
    can_access_legacy_node_settings?: boolean
    can_view_service_instance_details?: boolean
    can_manage_org_settings?: boolean
    can_view_service_instance_credentials?: boolean
    can_update_service_instance_credentials?: boolean
    can_test_service_instance_credentials?: boolean
    can_manage_credentials?: boolean
    can_access_communication?: boolean
    can_view_communication?: boolean
    can_manage_communication_providers?: boolean
    can_manage_communication_routing?: boolean
    can_test_communication?: boolean
    can_send_communication?: boolean
    can_manage_email_communication?: boolean
    can_manage_sms_communication?: boolean
    can_manage_whatsapp_communication?: boolean
    can_manage_slack_communication?: boolean
    can_access_tenant_directory?: boolean
    can_create_tenants?: boolean
    can_create_branches?: boolean
    can_create_vendors?: boolean
    can_create_suppliers?: boolean
    can_update_vendors?: boolean
    can_remove_vendors?: boolean
    can_update_suppliers?: boolean
    can_remove_suppliers?: boolean
    can_create_user_addresses?: boolean
    can_update_user_addresses?: boolean
    can_remove_user_addresses?: boolean
    can_set_default_user_addresses?: boolean
    can_access_user_directory?: boolean
    can_unlock_users?: boolean
    can_force_password_reset_users?: boolean
    can_create_users?: boolean
    can_update_users?: boolean
    can_approve_users?: boolean
    can_delete_users?: boolean
    can_manage_user_lifecycle?: boolean
    can_assign_user_roles?: boolean
    can_remove_user_roles?: boolean
    can_change_branch_admin?: boolean
    can_manage_branch_state?: boolean
    can_remove_branches?: boolean
    can_access_product_variants?: boolean
    can_access_templates?: boolean
    can_access_service_pos?: boolean
    can_access_branch_service_products?: boolean
    can_access_service_categories?: boolean
    can_access_devices?: boolean
    can_access_service_orders?: boolean
    can_access_service_offers?: boolean
    can_access_resource_planner?: boolean
    can_use_maps?: boolean
    can_access_job_management?: boolean
    can_access_jobs?: boolean
    can_access_job_categories?: boolean
    can_access_job_audit?: boolean
    can_access_job_customers?: boolean
    can_access_workers?: boolean
    can_access_vehicles?: boolean
    can_access_assets?: boolean
    can_access_maintenance_board?: boolean
    can_access_job_locations?: boolean
    can_access_attendance_board?: boolean
    can_access_assignments?: boolean
    can_access_worker_skills?: boolean
    can_access_worker_hotline?: boolean
    can_access_worker_tracking?: boolean
    can_access_teams?: boolean
  can_access_workdays?: boolean
  can_access_group_workday_access?: boolean
  can_access_job_configuration?: boolean
    can_access_job_workflows?: boolean
    can_access_job_status_transitions?: boolean
    can_access_finance?: boolean
    can_access_finance_reports?: boolean
    can_access_monthwise_reports?: boolean
    can_access_company_reports?: boolean
    can_access_worker_reports?: boolean
    can_access_client_reports?: boolean
    can_access_expenses?: boolean
    can_access_invoice_management?: boolean
    can_access_invoices?: boolean
    can_access_invoice_payments?: boolean
    can_access_inventory?: boolean
    can_access_worker_invoices?: boolean
    can_access_worker_payouts?: boolean
    can_access_company_payments?: boolean
    can_access_inter_company_settlement?: boolean
    can_grant_tenant_role_access?: boolean
    can_revoke_tenant_role_access?: boolean
    can_change_tenant_super_admin?: boolean
    can_access_user_documents?: boolean
    can_manage_user_documents?: boolean
    can_verify_user_documents?: boolean
    can_manage_templates?: boolean
    can_manage_communication_chat?: boolean
    can_manage_communication_email?: boolean
    can_manage_communication_sms?: boolean
    can_manage_communication_whatsapp?: boolean
    can_manage_communication_slack?: boolean
    can_manage_features?: boolean
    can_access_worker_attendance?: boolean
    can_access_expense_categories?: boolean
    can_access_expense_payments?: boolean
    can_manage_membership_payments?: boolean
    can_manage_membership_plans?: boolean
    can_manage_membership_subscriptions?: boolean
  } | null
}

export interface ActiveCurrency {
  symbol: string | null
  code: string | null
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  sessionId: string | null
  serviceInstanceId: string | null
  isAuthenticated: boolean
  hasHydrated: boolean
  currentBranch: Branch | null
  availableBranches: Branch[]
  currentPost: Post | null
  availablePosts: Post[]
  postsFetchedAt: number | null
  bookmarkedPostKeys: string[]
  recentPostKeys: string[]
  activeWorkerId: number | null
  isLoading: boolean
  // Currency configured for whichever service instance (org/tenant/branch) is
  // currently active. `currentBranch` is intentionally cleared for org/tenant
  // scoped posts (no single "current branch"), which left currency display
  // with nothing to read and defaulting to USD/$. This tracks the active
  // instance's own configured currency independent of that branch selection.
  activeCurrency: ActiveCurrency | null

  // Actions
  setUser: (user: User | null) => void
  setHasHydrated: (hydrated: boolean) => void
  setTokens: (access: string, refresh: string) => void
  setSessionId: (sessionId: string | null) => void
  setServiceInstanceId: (serviceInstanceId: string | null) => void
  setBranch: (branch: Branch | null) => void
  setBranches: (branches: Branch[]) => void
  setActiveCurrency: (currency: ActiveCurrency | null) => void
  setPost: (post: Post) => void
  clearPost: () => void
  setPosts: (posts: Post[], fetchedAt?: number) => void
  toggleBookmarkedPost: (post: Post) => void
  setActiveWorker: (workerId: number | null) => void
  setLoading: (loading: boolean) => void
  logout: () => void
  login: (
    user: User,
    access: string,
    refresh: string,
    posts?: Post[],
    sessionId?: string,
    serviceInstanceId?: string
  ) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      sessionId: null,
      serviceInstanceId: null,
      isAuthenticated: false,
      hasHydrated: false,
      currentBranch: null,
      availableBranches: [],
      currentPost: null,
      availablePosts: [],
      postsFetchedAt: null,
      bookmarkedPostKeys: [],
      recentPostKeys: [],
      activeWorkerId: null,
      isLoading: false,
      activeCurrency: null,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setActiveCurrency: (currency) => set({ activeCurrency: currency }),

      setHasHydrated: (hydrated) => set({ hasHydrated: hydrated }),
      
      setTokens: (access, refresh) => 
        set({ accessToken: access, refreshToken: refresh }),

      setSessionId: (sessionId) => set({ sessionId: sessionId || null }),

      setServiceInstanceId: (serviceInstanceId) => set({ serviceInstanceId }),
      
      setBranch: (branch) => set({ currentBranch: branch }),
      
      setBranches: (branches) => {
        const currentPost = get().currentPost

        if (isOrganizationScopePost(currentPost)) {
          set({ availableBranches: branches, currentBranch: null })
          return
        }

        const matchingPostBranch = findBranchForPost(branches, currentPost)
        if (matchingPostBranch) {
          set({ availableBranches: branches, currentBranch: matchingPostBranch })
          return
        }

        const currentBranch = get().currentBranch
        const currentBranchStillAvailable = currentBranch
          ? branches.some((branch) => String(branch.id) === String(currentBranch.id))
          : false

        set({
          availableBranches: branches,
          currentBranch: currentBranchStillAvailable ? currentBranch : null,
        })
      },
      
      setPost: (post) => {
        if (!isUsablePost(post)) {
          set({ currentPost: null, activeWorkerId: null })
          return
        }

        const postKey = getBookmarkedPostKey(post)
        const nextRecentPostKeys = postKey
          ? [postKey, ...get().recentPostKeys.filter((key) => key !== postKey)].slice(0, MAX_RECENT_POSTS)
          : get().recentPostKeys

        const previousPost = get().currentPost
        const postChanged = !previousPost || !isSamePost(previousPost, post)

        set({
          currentPost: post,
          recentPostKeys: nextRecentPostKeys,
          activeWorkerId: postChanged ? null : get().activeWorkerId,
          currentBranch: postChanged ? null : get().currentBranch,
          availableBranches: postChanged ? [] : get().availableBranches,
        })
      },

      clearPost: () => set({ currentPost: null }),
      
      setPosts: (posts, fetchedAt = Date.now()) => {
        const usablePosts = sanitizePosts(posts)
        const currentPost = get().currentPost
        const matchedCurrentPost = currentPost
          ? usablePosts.find((post) => isSamePost(post, currentPost)) || null
          : null

        // Keep the selected role stable across background refreshes even when
        // encrypted IDs or payload shape change between responses.
        // Preserve the existing encrypted id so layout hydrate / SidebarProvider
        // do not thrash on every getByToken / UserPostList refresh.
        const nextCurrentPost = currentPost
          ? (
            matchedCurrentPost
              ? {
                  ...currentPost,
                  ...matchedCurrentPost,
                  id: currentPost.id ?? matchedCurrentPost.id,
                  _id: currentPost._id ?? matchedCurrentPost._id,
                  capabilities: matchedCurrentPost.capabilities ?? currentPost.capabilities,
                  roles_data: {
                    ...(currentPost.roles_data ?? {}),
                    ...(matchedCurrentPost.roles_data ?? {}),
                  },
                }
              : (isBranchAdminPost(currentPost) ? replacementForHiddenBranchAdmin(usablePosts) : currentPost)
          )
          : null

        const nextBookmarkedPostKeys = get().bookmarkedPostKeys.filter((key) =>
          usablePosts.some((post) => getBookmarkedPostKey(post) === key),
        )
        const nextRecentPostKeys = get().recentPostKeys.filter((key) =>
          usablePosts.some((post) => getBookmarkedPostKey(post) === key),
        )

        set({
          availablePosts: usablePosts.map((post) => {
            if (!nextCurrentPost || !isSamePost(post, nextCurrentPost)) {
              return post
            }
            return {
              ...post,
              id: nextCurrentPost.id ?? post.id,
              _id: nextCurrentPost._id ?? post._id,
            }
          }),
          postsFetchedAt: fetchedAt,
          currentPost: nextCurrentPost,
          bookmarkedPostKeys: nextBookmarkedPostKeys,
          recentPostKeys: nextRecentPostKeys,
        })
      },

      toggleBookmarkedPost: (post) => {
        const bookmarkedPostKey = getBookmarkedPostKey(post)
        if (!bookmarkedPostKey) {
          return
        }

        const bookmarkedPostKeys = get().bookmarkedPostKeys
        const nextBookmarkedPostKeys = bookmarkedPostKeys.includes(bookmarkedPostKey)
          ? bookmarkedPostKeys.filter((key) => key !== bookmarkedPostKey)
          : [bookmarkedPostKey, ...bookmarkedPostKeys]

        set({ bookmarkedPostKeys: nextBookmarkedPostKeys })
      },
      
      setActiveWorker: (workerId) => set({ activeWorkerId: workerId }),
      
      setLoading: (loading) => set({ isLoading: loading }),
      
      logout: () => set({
        user: null,
        accessToken: null,
        refreshToken: null,
        sessionId: null,
        serviceInstanceId: null,
        isAuthenticated: false,
        hasHydrated: true,
        currentBranch: null,
        availableBranches: [],
        currentPost: null,
        availablePosts: [],
        postsFetchedAt: null,
        bookmarkedPostKeys: [],
        recentPostKeys: [],
        activeWorkerId: null,
        activeCurrency: null,
      }),
      
      login: (user, access, refresh, posts, sessionId, serviceInstanceId) => {
        const usablePosts = sanitizePosts(posts)

        set({
          user,
          accessToken: access,
          refreshToken: refresh,
          sessionId: sessionId || null,
          serviceInstanceId: serviceInstanceId || null,
          isAuthenticated: true,
          availablePosts: usablePosts,
          postsFetchedAt: usablePosts.length > 0 ? Date.now() : null,
          bookmarkedPostKeys: get().bookmarkedPostKeys.filter((key) =>
            usablePosts.some((post) => getBookmarkedPostKey(post) === key),
          ),
          recentPostKeys: get().recentPostKeys.filter((key) =>
            usablePosts.some((post) => getBookmarkedPostKey(post) === key),
          ),
          currentPost: null,
        })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        sessionId: state.sessionId,
        serviceInstanceId: state.serviceInstanceId,
        isAuthenticated: state.isAuthenticated,
        currentBranch: state.currentBranch,
        availableBranches: state.availableBranches,
        currentPost: state.currentPost,
        availablePosts: state.availablePosts,
        postsFetchedAt: state.postsFetchedAt,
        bookmarkedPostKeys: state.bookmarkedPostKeys,
        recentPostKeys: state.recentPostKeys,
        activeWorkerId: state.activeWorkerId,
        activeCurrency: state.activeCurrency,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)
